// Auslöser des Workflows: Art wählen und je nach Art Status bzw. Custom-Activity-Typ eintragen.
import type { Ausloeser, AusloeserArt, Bedingung } from "../api/workflows";
import { Auswahl } from "../components/Auswahl";
import { useMeta } from "../daten/meta";
import { Bedingungen } from "./Bedingungen";
import { AUSLOESER, leererAusloeser } from "./hilfen";

interface Props {
  ausloeser: Ausloeser;
  onChange: (a: Ausloeser) => void;
}

export function AusloeserEditor({ ausloeser, onChange }: Props) {
  const { meta } = useMeta();
  const c = ausloeser.config as { von?: string[]; nach?: string[]; type_id?: string | null; bedingungen?: Bedingung[] };
  const statuswahl = (meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }));
  const typen = (meta?.custom_activity_types ?? []).filter((t) => !t.is_archived || t.id === c.type_id);
  const set = (teil: Record<string, unknown>) => onChange({ ...ausloeser, config: { ...ausloeser.config, ...teil } });

  return (
    <div className="wf-form">
      <div className="wf-form__zeile">
        <div className="feld wf-feld">
          <span className="feld__label">Startet</span>
          <Auswahl
            optionen={AUSLOESER.map((a) => ({ wert: a.art, label: a.label, unter: a.text }))}
            wert={ausloeser.art}
            onWahl={(w) => w && w !== ausloeser.art && onChange(leererAusloeser(w as AusloeserArt))}
            breite={360}
          />
        </div>
      </div>

      {ausloeser.art === "statuswechsel" ? (
        <div className="wf-form__zeile">
          <div className="feld wf-feld">
            <span className="feld__label">Von Status (leer = jeder)</span>
            <Auswahl optionen={statuswahl} wert={c.von ?? []} mehrfach platzhalter="Beliebig" onWahl={(w) => set({ von: w })} />
          </div>
          <div className="feld wf-feld">
            <span className="feld__label">Nach Status</span>
            <Auswahl optionen={statuswahl} wert={c.nach ?? []} mehrfach platzhalter="Beliebig" onWahl={(w) => set({ nach: w })} />
          </div>
        </div>
      ) : null}

      {ausloeser.art === "custom_activity" ? (
        <>
          <div className="wf-form__zeile">
            <div className="feld wf-feld">
              <span className="feld__label">Typ der Custom Activity</span>
              <Auswahl
                optionen={typen.map((t) => ({ wert: t.id, label: t.name, unter: t.is_archived ? "archiviert" : null }))}
                wert={c.type_id ?? null}
                platzhalter="Typ wählen …"
                onWahl={(w) => set({ type_id: w, bedingungen: [] })}
              />
            </div>
          </div>
          {c.type_id ? (
            <div className="feld wf-feld wf-feld--breit">
              <span className="feld__label">Nur wenn (optional)</span>
              <Bedingungen quelle={{ aktivitaetTyp: c.type_id }} bedingungen={c.bedingungen ?? []} onChange={(b) => set({ bedingungen: b })} leerText="Jede gespeicherte Aktivität dieses Typs löst aus." />
            </div>
          ) : null}
          <p className="leise wf-form__text">Entwürfe lösen nichts aus. Wird ein Entwurf veröffentlicht, startet der Workflow dann.</p>
        </>
      ) : null}

      {ausloeser.art === "manuell" ? (
        <p className="leise wf-form__text">Gestartet wird über das Blitz-Symbol ▾ an jeder Smart View und am Lead („Workflow auslösen“), für markierte Leads oder alle Treffer.</p>
      ) : null}
      {ausloeser.art === "lead_angelegt" ? (
        <p className="leise wf-form__text">Gilt für Leads, die im CRM oder in Close neu entstehen. Ältere Leads, die der Abgleich nachträglich meldet, starten nichts.</p>
      ) : null}
      {ausloeser.art === "statuswechsel" ? (
        <p className="leise wf-form__text">Ein Statuswechsel zählt, egal ob von Hand, per Massenbearbeitung, durch einen anderen Workflow oder die Automatik. Wechsel älter als 3 Tage starten nichts mehr.</p>
      ) : null}
    </div>
  );
}
